import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import './AuthPage.css';

export default function AuthPage({ onGuestContinue }) {
  const { login, register, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return') || '/';

  const [tab, setTab] = useState('login');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [sundaySchoolRequest, setSundaySchoolRequest] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');

  if (isLoggedIn) {
    navigate(returnTo, { replace: true });
    return null;
  }

  async function handleResendVerification() {
    setResending(true);
    setResendMsg('');
    try {
      await api.resendVerification(verificationEmail);
      setResendMsg('Verification email sent! Check your inbox.');
    } catch (err) {
      setResendMsg(err.message);
    } finally {
      setResending(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      if (tab === 'login') {
        await login(email, password);
      } else {
        const result = await register(name, email, password, {
          sundaySchoolRequest,
        });
        if (result?.needsVerification) {
          setVerificationSent(true);
          setVerificationEmail(email);
          setSubmitting(false);
          return;
        }
      }
      if (onGuestContinue) onGuestContinue();
      else navigate(returnTo, { replace: true });
    } catch (err) {
      if (err.needsVerification) {
        setVerificationSent(true);
        setVerificationEmail(err.email || email);
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (verificationSent) {
    return (
      <main className="auth-page container">
        <div className="auth-card card">
          <h2 style={{ textAlign: 'center', marginBottom: 12 }}>Check your email</h2>
          <p style={{ textAlign: 'center', color: 'var(--gray-500)', marginBottom: 16 }}>
            We sent a verification link to <strong>{verificationEmail}</strong>. Please check your inbox and click the link to verify your account.
          </p>
          <button
            className="btn btn-primary btn-block"
            onClick={handleResendVerification}
            disabled={resending}
          >
            {resending ? 'Sending...' : 'Resend verification email'}
          </button>
          {resendMsg && <p style={{ textAlign: 'center', marginTop: 10, fontSize: 14, color: 'var(--gray-500)' }}>{resendMsg}</p>}
          <button
            type="button"
            className="auth-guest-link"
            style={{ marginTop: 16 }}
            onClick={() => { setVerificationSent(false); setError(null); }}
          >
            Back to sign in
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page container">
      <div className="auth-card card">
        <div className="auth-tabs">
          <button
            className={`auth-tab ${tab === 'login' ? 'active' : ''}`}
            onClick={() => { setTab('login'); setError(null); }}
          >
            Sign In
          </button>
          <button
            className={`auth-tab ${tab === 'register' ? 'active' : ''}`}
            onClick={() => { setTab('register'); setError(null); }}
          >
            Create Account
          </button>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {tab === 'register' && (
            <div className="form-group">
              <label htmlFor="auth-name">Name</label>
              <input
                id="auth-name"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoComplete="name"
              />
            </div>
          )}

          <div className="form-group">
            <label htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              type="password"
              placeholder={tab === 'register' ? 'At least 6 characters' : 'Your password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={tab === 'register' ? 6 : undefined}
              autoComplete={tab === 'register' ? 'new-password' : 'current-password'}
            />
            {tab === 'login' && (
              <div className="auth-forgot-wrap">
                <Link to="/forgot-password" className="auth-forgot-link">
                  Forgot password?
                </Link>
              </div>
            )}
          </div>

          {tab === 'register' && (
            <div className="form-group auth-ss-request">
              <label className="auth-checkbox-label">
                <input
                  type="checkbox"
                  checked={sundaySchoolRequest}
                  onChange={(e) => setSundaySchoolRequest(e.target.checked)}
                />
                <span>
                  I am a Sunday School servant — request cafe priority
                  <small>
                    An administrator must approve this before your orders show
                    “SUNDAY SCHOOL” on cafe stickers.
                  </small>
                </span>
              </label>
            </div>
          )}

          {error && <p className="error-text">{error}</p>}

          <button
            type="submit"
            className="btn btn-primary btn-block auth-submit"
            disabled={submitting}
          >
            {submitting
              ? (tab === 'login' ? 'Signing in...' : 'Creating account...')
              : (tab === 'login' ? 'Sign In' : 'Create Account')}
          </button>
        </form>

        <button
          type="button"
          className="auth-guest-link"
          onClick={() => { if (onGuestContinue) onGuestContinue(); else navigate(returnTo); }}
        >
          Continue as Guest
        </button>

        <p className="auth-privacy">
          <a href="/privacy-policy.html">Privacy policy</a>
        </p>
      </div>
    </main>
  );
}
